import { fromSerial, readSheet, type SheetRow } from './xlsx'
import { ScheduleError, type ImportScope, type ParsedFile, type RawLeg } from './types'

/**
 * The AIMS daily flight schedule report.
 *
 * Layout as shipped:
 *
 *   Daily Flight Schedule Report (01/09/2026-30/09/2026)
 *   Times in Local Station - All times based on MVT/ACARS/Manual Entries
 *   DATE  FLT  TYPE  REG  AC  DEP  ARR  STD  STA  ETD  ETA  BLOCK
 *
 * Two lines of it decide how the whole import behaves:
 *
 * 1. **"Times in Local Station"** — every clock in the file is local at the
 *    station it belongs to, so STD and STA are in two different zones on any
 *    international leg. Nothing is stored until both have been resolved to UTC
 *    (see resolve.ts).
 * 2. **The period in the title** — the file covers 01/09 to 30/09 whether or
 *    not every one of those days has a row. That is what makes a re-import
 *    able to withdraw flights: a day inside the period with no rows is an
 *    empty day, not a day nobody mentioned.
 *
 * Columns are found by their heading rather than by position, so a report with
 * an extra column still reads.
 */

/** Heading -> field. Anything else in the row is ignored. */
const HEADINGS: Record<string, keyof HeaderMap> = {
  DATE: 'date',
  FLT: 'flight',
  'FLT NO': 'flight',
  FLIGHT: 'flight',
  TYPE: 'serviceType',
  REG: 'regn',
  REGN: 'regn',
  'A/C REG': 'regn',
  AC: 'acType',
  'A/C': 'acType',
  DEP: 'dep',
  FROM: 'dep',
  ARR: 'arr',
  TO: 'arr',
  STD: 'std',
  STA: 'sta',
  ETD: 'etd',
  ETA: 'eta',
  BLOCK: 'block',
}

interface HeaderMap {
  date?: string
  flight?: string
  serviceType?: string
  regn?: string
  acType?: string
  dep?: string
  arr?: string
  std?: string
  sta?: string
  etd?: string
  eta?: string
  block?: string
}

export async function parseDayReport(file: File): Promise<ParsedFile> {
  const rows = await readSheet(await file.arrayBuffer(), file.name)
  return readRows(rows, file.name)
}

/** Split out so the same reader can be driven from a test or a script. */
export function readRows(rows: SheetRow[], fileName: string): ParsedFile {
  const headerIndex = rows.findIndex((row) => headerOf(row) !== null)
  if (headerIndex < 0) throw new ScheduleError('unknown_layout', fileName)

  const header = headerOf(rows[headerIndex]) as HeaderMap
  const missing = (['date', 'flight', 'dep', 'arr', 'std', 'sta'] as const).filter(
    (field) => !header[field],
  )
  if (missing.length > 0) throw new ScheduleError('unknown_layout', fileName)

  const legs: RawLeg[] = []
  const skipped: ParsedFile['skipped'][number][] = []

  for (const row of rows.slice(headerIndex + 1)) {
    // Page furniture and repeated headings sit in the same columns as data.
    if (headerOf(row)) continue
    const cell = (field: keyof HeaderMap) => {
      const column = header[field]
      return column ? (row.cells[column] ?? '') : ''
    }

    const rawDate = cell('date')
    const flight = cell('flight').trim()
    const dep = cell('dep').trim().toUpperCase()
    const arr = cell('arr').trim().toUpperCase()
    if (!rawDate && !flight && !dep) continue

    const operatingDate = parseDate(rawDate)
    if (!operatingDate) {
      skipped.push({ row: row.row, reason: 'date' })
      continue
    }
    if (!flight || dep.length !== 3 || arr.length !== 3) {
      skipped.push({ row: row.row, reason: 'sector' })
      continue
    }
    const std = parseClock(cell('std'))
    const sta = parseClock(cell('sta'))
    if (!std || !sta) {
      skipped.push({ row: row.row, reason: 'time' })
      continue
    }

    legs.push({
      operatingDate,
      flightNumber: flight.replace(/\s+/g, ' '),
      serviceType: cell('serviceType').trim().toUpperCase(),
      regn: cell('regn').trim().toUpperCase(),
      acType: cell('acType').trim().toUpperCase(),
      dep,
      arr,
      std,
      sta,
      etd: parseClock(cell('etd')) ?? '',
      eta: parseClock(cell('eta')) ?? '',
      blockMinutes: parseDuration(cell('block')),
    })
  }

  if (legs.length === 0) throw new ScheduleError('empty_file', fileName)

  const declared = declaredScope(rows.slice(0, headerIndex))
  return {
    fileName,
    scope: declared ?? spanOf(legs),
    scopeDeclared: declared !== null,
    legs,
    skipped,
  }
}

/** A row is the header when it names at least the date and the flight. */
function headerOf(row: SheetRow): HeaderMap | null {
  const map: HeaderMap = {}
  for (const [column, value] of Object.entries(row.cells)) {
    const field = HEADINGS[value.trim().toUpperCase()]
    if (field && !map[field]) map[field] = column
  }
  return map.date && map.flight ? map : null
}

/**
 * The period the report states in its own title.
 *
 * Preferred over the span of the rows because it covers days the file says are
 * empty. Importing a period that ends on a cancelled day would otherwise leave
 * that day's withdrawn flights standing.
 */
function declaredScope(head: SheetRow[]): ImportScope | null {
  for (const row of head) {
    for (const value of Object.values(row.cells)) {
      const match = value.match(
        /(\d{1,2}[/-]\d{1,2}[/-]\d{2,4})\s*(?:-|–|—|to)\s*(\d{1,2}[/-]\d{1,2}[/-]\d{2,4})/i,
      )
      if (!match) continue
      const from = parseDate(match[1])
      const to = parseDate(match[2])
      if (from && to && from <= to) return { from, to }
    }
  }
  return null
}

function spanOf(legs: RawLeg[]): ImportScope {
  let from = legs[0].operatingDate
  let to = legs[0].operatingDate
  for (const leg of legs) {
    if (leg.operatingDate < from) from = leg.operatingDate
    if (leg.operatingDate > to) to = leg.operatingDate
  }
  return { from, to }
}

/**
 * "01/09/26", "01/09/2026", "2026-09-01" or an Excel serial -> "2026-09-01".
 *
 * Day first: the report is written for an operator that reads it that way, and
 * the file carries no locale to say otherwise. A two-digit year is this
 * century — these files are current schedules, never historical ones.
 */
export function parseDate(value: string): string | null {
  const text = value.trim()
  if (!text) return null

  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`

  const parts = text.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})/)
  if (parts) {
    const day = Number(parts[1])
    const month = Number(parts[2])
    const year = Number(parts[3])
    if (day < 1 || day > 31 || month < 1 || month > 12) return null
    const full = year < 100 ? 2000 + year : year
    return `${full}-${pad(month)}-${pad(day)}`
  }

  if (/^\d+(\.\d+)?$/.test(text)) {
    const parsed = fromSerial(Number(text))
    return `${parsed.year}-${pad(parsed.month)}-${pad(parsed.day)}`
  }
  return null
}

/** "8:00", "08:00", "0800" or an Excel serial fraction -> "08:00". */
export function parseClock(value: string): string | null {
  const text = value.trim()
  if (!text) return null

  const colon = text.match(/^(\d{1,2}):(\d{2})/)
  if (colon) {
    const hour = Number(colon[1])
    const minute = Number(colon[2])
    if (hour > 23 || minute > 59) return null
    return `${pad(hour)}:${pad(minute)}`
  }

  if (/^\d{3,4}$/.test(text)) {
    const hour = Number(text.slice(0, text.length - 2))
    const minute = Number(text.slice(-2))
    if (hour > 23 || minute > 59) return null
    return `${pad(hour)}:${pad(minute)}`
  }

  if (/^\d*\.\d+$/.test(text)) {
    const parsed = fromSerial(Number(text))
    return `${pad(parsed.hour)}:${pad(parsed.minute)}`
  }
  return null
}

/** "5:28" -> 328 minutes. Block time can exceed a day on no leg here, but may. */
export function parseDuration(value: string): number {
  const text = value.trim()
  const colon = text.match(/^(\d{1,3}):(\d{2})/)
  if (colon) return Number(colon[1]) * 60 + Number(colon[2])
  if (/^\d*\.\d+$/.test(text)) return Math.round(Number(text) * 1440)
  return 0
}

function pad(value: number): string {
  return value < 10 ? `0${value}` : String(value)
}
